/**
 * Shared helper that translates a field's cfg (x, y, textAlign) into the
 * actual CSS positioning props. Used by both the admin cover template
 * editor and the promoter live preview so the two renders never drift.
 *
 * Semantics:
 *   - textAlign: 'left'   → cfg.x is the LEFT edge of the text
 *   - textAlign: 'center' → cfg.x is the CENTER of the text  (translateX -50%)
 *   - textAlign: 'right'  → cfg.x is the RIGHT edge of the text (translateX -100%)
 *
 * This is the semantic the admin intuitively expects when picking an
 * alignment: a centered title with x=50 truly sits at horizontal center.
 */
export const getFieldPositionStyles = (cfg = {}) => {
  const x = cfg.x ?? 50;
  const align = cfg.textAlign || 'left';
  // Symmetric max width — the bounding box never escapes either side of
  // the canvas. Generous (no extra margin) so text fields are as wide
  // as physically possible: text wraps less and auto-shrink kicks in
  // only when truly necessary.
  const maxWidthPct =
    align === 'center'
      ? Math.max(10, Math.min(x, 100 - x) * 2)
      : align === 'right'
        ? Math.max(10, x)
        : Math.max(10, 100 - x);
  const transformX =
    align === 'center' ? '-50%' :
    align === 'right'  ? '-100%' :
    '0%';
  return {
    left: `${x}%`,
    top: `${cfg.y ?? 50}%`,
    maxWidth: `${maxWidthPct}%`,
    transform: `translateX(${transformX})`,
    textAlign: align,
  };
};
